'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Transition } from '@headlessui/react';
import CustomizeImage from '@/public/images/features-home-customize.jpg';
import OrganizeImage from '@/public/images/features-home-organize.jpg';
import RememberImage from '@/public/images/features-home-remember.jpg';
import EncryptImage from '@/public/images/features-home-encrypt.jpg';

export default function FeaturesHome({
  bandClassName = 'bg-slate-100',
}: {
  bandClassName?: string;
}) {
  const [tab, setTab] = useState<number>(1);

  const tabs = useRef<HTMLDivElement>(null);

  const heightFix = () => {
    if (tabs.current && tabs.current.parentElement)
      tabs.current.parentElement.style.height = `${tabs.current.clientHeight}px`;
  };

  useEffect(() => {
    heightFix();
  }, []);

  return (
    <section className="relative">
      <div
        className={`absolute inset-0 ${bandClassName} pointer-events-none mb-64 md:mb-80`}
        aria-hidden="true"
      ></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="py-12 md:py-20">
          {/* Section header */}
          <div className="max-w-3xl mx-auto text-center pb-12">
            <h2 className="h2 font-merriweather text-zinc-800 mb-4">Built for journaling</h2>
            <p className="text-xl text-slate-768">
              Penzu was built specifically for recording your daily life in a{' '}
              <a href="/how-to-write-a-reflective-journal" className="underline underline-offset-4">
                reflective journal
              </a>
              , keeping track of milestones in a{' '}
              <a href="/food-diary" className="underline underline-offset-4">
                food diary
              </a>{' '}
              or{' '}
              <a href="/pregnancy-journal" className="underline underline-offset-4">
                pregnancy journal
              </a>
              , or even recording your dreams in a{' '}
              <a href="/dream-journal" className="underline underline-offset-4">
                dream journal
              </a>{' '}
              &mdash; or just write freely.
            </p>
          </div>

          {/* Section content */}
          <div className="max-w-3xl mx-auto">
            {/* Tabs buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pb-12">
              {/* Customize button */}
              <button
                className={`text-center transition-opacity ${
                  tab !== 1 && 'opacity-50 hover:opacity-75'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(1);
                }}
              >
                <div>
                  <div className="inline-flex bg-white rounded-full shadow-md mb-3">
                    <svg width="56" height="56" xmlns="http://www.w3.org/2000/svg">
                      <path fillOpacity=".64" fill="#5091EE" d="M33 23v8h3V20H25v3z" />
                      <path fill="#2174EA" d="M20 25h11v11H20z" />
                    </svg>
                  </div>
                  <div className="md:text-lg leading-tight font-semibold text-zinc-800">
                    Customize
                  </div>
                </div>
              </button>

              {/* Organize button */}
              <button
                className={`text-center transition-opacity ${
                  tab !== 2 && 'opacity-50 hover:opacity-75'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(2);
                }}
              >
                <div>
                  <div className="inline-flex bg-white rounded-full shadow-md mb-3">
                    <svg width="56" height="56" xmlns="http://www.w3.org/2000/svg">
                      <path fillOpacity=".64" fill="#5091EE" d="M20 27l7-7h-7z" />
                      <path fill="#2174EA" d="M29 20l7 7v-7z" />
                      <path fillOpacity=".64" fill="#5091EE" d="M36 29l-7 7h7z" />
                      <path fill="#2174EA" d="M27 36l-7-7v7z" />
                    </svg>
                  </div>
                  <div className="md:text-lg leading-tight font-semibold text-zinc-800">
                    Organize
                  </div>
                </div>
              </button>

              {/* Remember button */}
              <button
                className={`text-center transition-opacity ${
                  tab !== 3 && 'opacity-50 hover:opacity-75'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(3);
                }}
              >
                <div>
                  <div className="inline-flex bg-white rounded-full shadow-md mb-3">
                    <svg width="56" height="56" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21 20C19.1937 20 19 20.0728 19 21.0714V32.8571C19 33.6071 19 34.4643 19 35L21 32.7143C21 29.9167 21 22.0833 21 20Z"
                        fill="#D9E9FB"
                      />
                      <path
                        d="M31 19H23C22.4 19 22 19.4571 22 20.1429V32.7143C22 33.5143 22 34.4286 22 35L27 30.4286L32 35C32 35 32 33.5143 32 31.5714V20.1429C32 19.4571 31.6 19 31 19Z"
                        fill="#2174EA"
                      />
                    </svg>
                  </div>
                  <div className="md:text-lg leading-tight font-semibold text-zinc-800">
                    Remember
                  </div>
                </div>
              </button>

              {/* Encrypt button */}
              <button
                className={`text-center transition-opacity ${
                  tab !== 4 && 'opacity-50 hover:opacity-75'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(4);
                }}
              >
                <div>
                  <div className="inline-flex bg-white rounded-full shadow-md mb-3">
                    <svg width="56" height="56" xmlns="http://www.w3.org/2000/svg">
                      <rect x="20.5" y="25.2249" width="14.9934" height="11.8202" fill="#2174EA" />
                      <ellipse cx="28.25" cy="30.5" rx="1" ry="1.5" fill="#D3E5FF" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M23.3806 25.423C23.3162 24.3255 23.4941 22.6862 24.2106 21.3423C24.8866 20.0744 26.0352 19.0689 27.9841 19.0034C29.9331 19.0689 31.0817 20.0744 31.7577 21.3423C32.4742 22.6862 32.6521 24.3255 32.5876 25.423L33.5859 25.4816C33.6575 24.2617 33.468 22.4248 32.6401 20.8719C31.842 19.3749 30.4453 18.1442 28.1882 18.0118V18C28.1195 18 28.0514 18.001 27.9841 18.0029C27.9168 18.001 27.8488 18 27.78 18V18.0118C25.5229 18.1442 24.1262 19.3749 23.3281 20.8719C22.5002 22.4248 22.3107 24.2617 22.3824 25.4816L23.3806 25.423Z"
                        fill="#2174EA"
                      />
                      <path
                        d="M24.2106 21.3423L23.7694 21.1071L23.7694 21.1071L24.2106 21.3423ZM23.3806 25.423L23.4099 25.9222L23.9091 25.8928L23.8798 25.3937L23.3806 25.423ZM27.9841 19.0034L28.0009 18.5037L27.9841 18.5031L27.9673 18.5037L27.9841 19.0034ZM31.7577 21.3423L32.1989 21.1071L32.1989 21.1071L31.7577 21.3423ZM32.5876 25.423L32.0885 25.3937L32.0592 25.8928L32.5583 25.9222L32.5876 25.423ZM33.5859 25.4816L33.5566 25.9808L34.0557 26.0101L34.0851 25.5109L33.5859 25.4816ZM32.6401 20.8719L33.0813 20.6367L33.0813 20.6367L32.6401 20.8719ZM28.1882 18.0118H27.6882V18.4833L28.159 18.5109L28.1882 18.0118ZM28.1882 18H28.6882V17.5H28.1882V18ZM27.9841 18.0029L27.9698 18.5027L27.9841 18.5031L27.9985 18.5027L27.9841 18.0029ZM27.78 18V17.5H27.28V18H27.78ZM27.78 18.0118L27.8093 18.5109L28.28 18.4833V18.0118H27.78ZM23.3281 20.8719L23.7694 21.1071L23.3281 20.8719ZM22.3824 25.4816L21.8832 25.5109L21.9125 26.0101L22.4117 25.9808L22.3824 25.4816ZM23.7694 21.1071C22.9972 22.5555 22.8135 24.2936 22.8815 25.4523L23.8798 25.3937C23.8189 24.3575 23.991 22.817 24.6518 21.5776L23.7694 21.1071ZM27.9673 18.5037C25.8105 18.5762 24.5146 19.7093 23.7694 21.1071L24.6518 21.5776C25.2585 20.4395 26.2599 19.5616 28.0009 19.5031L27.9673 18.5037ZM27.9673 19.5031C29.7084 19.5616 30.7098 20.4395 31.3165 21.5776L32.1989 21.1071C31.4537 19.7093 30.1578 18.5762 28.0009 18.5037L27.9673 19.5031ZM31.3165 21.5776C31.9773 22.817 32.1493 24.3575 32.0885 25.3937L33.0868 25.4523C33.1548 24.2936 32.9711 22.5555 32.1989 21.1071L31.3165 21.5776ZM32.5583 25.9222L33.5566 25.9808L33.6152 24.9825L32.6169 24.9239L32.5583 25.9222ZM34.0851 25.5109C34.1603 24.2297 33.965 22.2941 33.0813 20.6367L32.1989 21.1071C32.9711 22.5555 33.1548 24.2936 33.0868 25.4523L34.0851 25.5109ZM33.0813 20.6367C32.2164 19.0143 30.6783 17.657 28.2175 17.5127L28.159 18.5109C30.2123 18.6314 31.4677 19.7355 32.1989 21.1071L33.0813 20.6367ZM27.6882 18V18.0118H28.6882V18H27.6882ZM27.9985 18.5027C28.0609 18.5009 28.1242 18.5 28.1882 18.5V17.5C28.1147 17.5 28.0419 17.501 27.9698 17.5031L27.9985 18.5027ZM27.9985 17.5031C27.9263 17.501 27.8535 17.5 27.78 17.5V18.5C27.8441 18.5 27.9073 18.5009 27.9698 18.5027L27.9985 17.5031ZM27.28 18V18.0118H28.28V18H27.28ZM23.7694 21.1071C24.5006 19.7355 25.756 18.6314 27.8093 18.5109L27.7507 17.5127C25.2899 17.657 23.7519 19.0143 22.8869 20.6367L23.7694 21.1071ZM22.8815 25.4523C22.8135 24.2936 22.9972 22.5555 23.7694 21.1071L22.8869 20.6367C22.0033 22.2941 21.808 24.2297 21.8832 25.5109L22.8815 25.4523ZM23.3513 24.9239L22.353 24.9825L22.4117 25.9808L23.4099 25.9222L23.3513 24.9239Z"
                        fill="#2174EA"
                      />
                    </svg>
                  </div>
                  <div className="md:text-lg leading-tight font-semibold text-zinc-800">
                    Encrypt
                  </div>
                </div>
              </button>
            </div>

            {/* Tabs items */}
            <div className="transition-all">
              <div className="relative flex flex-col" data-aos="fade-up" ref={tabs}>
                {/* Item 1 */}
                <Transition
                  show={tab === 1}
                  enter="transition ease-in-out duration-700 transform order-first"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  beforeEnter={() => heightFix()}
                >
                  <Image
                    className="mx-auto shadow-2xl"
                    src={CustomizeImage}
                    width={768}
                    height={474}
                    alt="Customize preview"
                  />
                </Transition>
                {/* Item 2 */}
                <Transition
                  show={tab === 2}
                  enter="transition ease-in-out duration-700 transform order-first"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  beforeEnter={() => heightFix()}
                >
                  <Image
                    className="mx-auto shadow-2xl"
                    src={OrganizeImage}
                    width={768}
                    height={474}
                    alt="Organize preview"
                  />
                </Transition>
                {/* Item 3 */}
                <Transition
                  show={tab === 3}
                  enter="transition ease-in-out duration-700 transform order-first"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  beforeEnter={() => heightFix()}
                >
                  <Image
                    className="mx-auto shadow-2xl"
                    src={RememberImage}
                    width={768}
                    height={474}
                    alt="Remember preview"
                  />
                </Transition>
                {/* Item 4 */}
                <Transition
                  show={tab === 4}
                  enter="transition ease-in-out duration-700 transform order-first"
                  enterFrom="opacity-0 -translate-y-16"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in-out duration-300 transform absolute"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-16"
                  beforeEnter={() => heightFix()}
                >
                  <Image
                    className="mx-auto shadow-2xl"
                    src={EncryptImage}
                    width={768}
                    height={474}
                    alt="Encrypt preview"
                  />
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
